import { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { modules } from '@veritone/glc-redux';

import callGraphQLApi from '@helpers/callGraphQLApi';
import { fetchGraphQLApi } from '@helpers/callApi';
import { resolveRenamedRegistryId } from '@helpers/registryConfig';
import {
  DELETE_ORGANIZATION_SDO_FAILURE,
  DELETE_ORGANIZATION_SDO_SUCCESS,
  DELETE_SDO_BULK_FAILURE,
  DELETE_SDO_BULK_SUCCESS,
  DELETE_TDO_BULK_FAILURE,
  DELETE_TDO_BULK_SUCCESS,
  FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE,
  FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS,
  FETCH_ORGANIZATION_SDOS_FAILURE,
  FETCH_ORGANIZATION_SDOS_SUCCESS,
  FETCH_ORGANIZATION_TDO_BY_ID_FAILURE,
  FETCH_ORGANIZATION_TDO_BY_ID_SUCCESS,
  FETCH_ORGANIZATION_TDOS_FAILURE,
  FETCH_ORGANIZATION_TDOS_SUCCESS,
  NOOP_ORGANIZATION_ACTION,
  UPSERT_ORGANIZATION_SDO_FAILURE,
  UPSERT_ORGANIZATION_SDO_SUCCESS,
  FETCH_ORGANIZATION_TASKS_SUCCESS,
  FETCH_ORGANIZATION_TASKS_FAILURE,
  FETCH_ORGANIZATION_JOBS_SUCCESS,
  FETCH_ORGANIZATION_JOBS_FAILURE,
  SOFT_DELETE_TDO_BULK_SUCCESS,
  SOFT_DELETE_TDO_BULK_FAILURE,
} from './actions';
import { TDOId } from '@common-modules/universal/models/Brands';
import type { JobStatus, TaskStatus } from 'veritone-types';
import { SummaryTDO, VeritoneFile } from './models';
import moment from 'moment';
import { selectUser } from '@helpers/tdoHelper';
import { getLatestSchemaId } from '@common/state/modules/notification/services';
import { cloneDeep, noop } from 'lodash';
import { Tag } from '@common-modules/mediaDetails/models';
import {
  removeKeyQuotes,
  FolderPathItem,
  tdoFolderChain,
} from '@redact-modules/helper';
import { selectV2FoldersEnabled } from '@common/user-onboarding/selectors/organization';
import {
  DEFAULT_REDACTION_STATUS_FILTER,
  resolveRedactionStatusKey,
  getRedactionStatusDisplayLabel,
} from '@common-modules/mediaDetails/redactionStatuses';

export type Thunk<P, R = any> = (
  payload: P,
  ...args: any[]
) => ThunkAction<Promise<R | undefined>, any, unknown, UnknownAction>;

const {
  config: { getConfig },
  auth: { selectOAuthToken, selectSessionToken },
} = modules;

interface UpsertOrganizationSDO {
  tdoId: TDOId;
  /** Legacy schema key still required by current Redact File Status SDO schema. */
  status?: string;
  statusLabel?: string;
  statusKey?: string;
  isCustomStatus?: boolean;
}

interface ExtraUpsertOrganizationSDO {
  description: string;
  modifiedDateTime: string;
  createdDateTime: string;
  engineCategoriesRun: string[];
  sourceTDO: TDOId;
}

const getSchemaCompatibleUpsertData = (
  data: UpsertOrganizationSDO & Partial<ExtraUpsertOrganizationSDO>
): UpsertOrganizationSDO & Partial<ExtraUpsertOrganizationSDO> => {
  const resolvedKey =
    resolveRedactionStatusKey(data.statusKey || data.statusLabel) ||
    DEFAULT_REDACTION_STATUS_FILTER;
  const statusLabel = getRedactionStatusDisplayLabel({
    statusKey: resolvedKey,
    statusLabel: data.statusLabel,
    isCustomStatus: data.isCustomStatus,
  });

  return {
    ...data,
    // Keep legacy field for backward-compatible schema validation.
    status: statusLabel,
    statusKey: resolvedKey,
    statusLabel,
    isCustomStatus: data.isCustomStatus || false,
  };
};

/**
 * Upsert an SDO. Leave id undefined for insert.
 */
export const upsertOrganizationSDO: Thunk<{
  readonly id?: string;
  readonly data: UpsertOrganizationSDO & Partial<ExtraUpsertOrganizationSDO>;
}> =
  ({ id, data }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation upsertSDOs($id: ID, $schemaId: ID!, $data: JSONData!) {
        createStructuredData(input: {
          id: $id,
          schemaId: $schemaId,
          data: $data
        }) {
          id
          data
          createdDateTime
          modifiedDateTime
        }
      }
    `;

    const compatibleData = getSchemaCompatibleUpsertData(data);

    return await callGraphQLApi<{
      createStructuredData: {
        id: string;
        data: any;
        createdDateTime: string;
        modifiedDateTime: string;
      };
    }>({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        UPSERT_ORGANIZATION_SDO_SUCCESS,
        UPSERT_ORGANIZATION_SDO_FAILURE,
      ],
      query,
      variables: { schemaId, id, data: compatibleData },
      dispatch,
      getState,
    });
  };

// /**
//  * Delete an SDO by ID.
//  * @param request - SDO Id and TDO Id required.
//  */
export const deleteOrganizationSDO: Thunk<{
  readonly id: string;
  readonly tdoId: TDOId;
}> =
  ({ id, tdoId }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      mutation deleteSDO($id: ID!, $schemaId: ID!) {
        deleteStructuredData(input: {
          id: $id,
          schemaId: $schemaId,
        }) {
          id
        }
      }
    `;

    return await callGraphQLApi<{ deleteStructuredData: { id: string } }>({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        DELETE_ORGANIZATION_SDO_SUCCESS,
        DELETE_ORGANIZATION_SDO_FAILURE,
      ],
      query,
      variables: { schemaId, id, tdoId },
      dispatch,
      getState,
    });
  };

export type DeleteSDOBulkResponse = Record<
  string,
  {
    id: string;
  }
>;

export type DeleteSDOBulkFailureResponse = Array<{
  message: string;
  name: string;
  time_thrown: string;
  data: {
    objectId: TDOId;
    objectType: 'StructuredDataObject';
    errorId: string;
    requestId: string;
  };
  path: string[];
  locations: {
    line: number;
    column: number;
  }[];
}>;

/**
 * Delete multiple SDOs by ID
 */
export const deleteSDOBulk: Thunk<
  {
    readonly sdoIds: string[];
    /**
     * Skip the shared terminal actions (VE-28551). DELETE_SDO_BULK_FAILURE has
     * a global watcher that raises "An error occurred while unlinking one or
     * more files" — right for an unlink the user just asked for, wrong for a
     * best-effort purge that runs AFTER a delete they were already told
     * succeeded. Silent callers read the returned value instead.
     */
    readonly silent?: boolean;
  },
  DeleteSDOBulkResponse
> =
  ({ sdoIds, silent }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `mutation deleteSDOs {
      ${sdoIds
        .map(
          (sdoId, index) => `
      i${index}: deleteStructuredData(
        input: {
          id: "${sdoId}",
          schemaId: "${schemaId}"
        }) {
        id
      }`
        )
        .join(',')}
    }`;

    return await callGraphQLApi<DeleteSDOBulkResponse>({
      actionTypes: silent
        ? [
            NOOP_ORGANIZATION_ACTION,
            NOOP_ORGANIZATION_ACTION,
            NOOP_ORGANIZATION_ACTION,
          ]
        : [
            NOOP_ORGANIZATION_ACTION,
            DELETE_SDO_BULK_SUCCESS,
            DELETE_SDO_BULK_FAILURE,
          ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

export type DeleteTDOBulkResponse = Record<string, { id: string }>;
export type DeleteTDOBulkFailureResponse = Array<{
  message: string;
  name: string;
  time_thrown: string;
  data: {
    objectId: TDOId;
    objectType: 'TemporalDataObject';
    errorId: string;
    requestId: string;
  };
  path: string[];
  locations: {
    line: number;
    column: number;
  }[];
}>;

const buildTdoDetails = (
  tdo: SummaryTDO,
  trashRetentionDays: number,
  movedToTrashByName: string,
  movedToTrashById: string,
  isV2Folders = false
) => {
  const updatedTags = cloneDeep([...(tdo?.details?.tags || [])]);
  // VE-21114: rebuild the full chain (v2 drops the leaf) so the stamped
  // "Original Location" matches the v1 shape the Trash tables expect.
  const oldFolderPath = tdoFolderChain(tdo?.folders?.[0], isV2Folders);
  // TODO: Why is it valid to just put this on the first tag?
  if (updatedTags?.[0]) {
    const updatedTag = {
      ...updatedTags[0],
      trashRetentionDays: String(trashRetentionDays),
      movedToTrashDateTime: moment().toISOString(),
      movedToTrashByName,
      movedToTrashById,
      ...(oldFolderPath?.length ? { oldFolderPath } : {}),
    };
    updatedTags[0] = updatedTag;
  }
  return removeKeyQuotes(
    JSON.stringify({
      ...(tdo?.details || {}),
      tags: updatedTags,
    })
  );
};
/**
 * Delete multiple TDOs by ID
 */
export const softDeleteTDOBulk: Thunk<{
  readonly tdos: SummaryTDO[];
  readonly trashRetentionDays: number;
}> =
  ({ tdos, trashRetentionDays }) =>
  async (dispatch, getState) => {
    // Record who moved the files to trash, alongside the timestamp.
    const user = selectUser(getState());
    const movedToTrashByName = user?.userName || user?.email || '';
    const movedToTrashById = user?.userId || '';
    const isV2Folders = selectV2FoldersEnabled(getState());
    const query = `mutation softDeleteTDOs {
      ${tdos
        .map(
          (tdo, index) => `i${index}:updateTDO(input: {id:${
            tdo.id
          }, details: ${buildTdoDetails(
            tdo,
            trashRetentionDays,
            movedToTrashByName,
            movedToTrashById,
            isV2Folders
          )}})
              {
                id
              }`
        )
        .join(',')}
    }`;

    return await callGraphQLApi<DeleteTDOBulkResponse>({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        SOFT_DELETE_TDO_BULK_SUCCESS,
        SOFT_DELETE_TDO_BULK_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

/**
 * Delete multiple TDOs by ID
 */
export const deleteTDOBulk: Thunk<{
  readonly tdoIds: string[];
}> =
  ({ tdoIds }) =>
  async (dispatch, getState) => {
    const query = `mutation deleteTDOs {
      ${tdoIds
        .map(
          (tdoId, index) => `
          i${index}: deleteTDO(id: "${tdoId}") {
            id
          }`
        )
        .join(',')}
    }`;

    return await callGraphQLApi<DeleteTDOBulkResponse>({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        DELETE_TDO_BULK_SUCCESS,
        DELETE_TDO_BULK_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
    });
  };

export interface FetchOrganizationSDOsResponse {
  structuredDataObjects: {
    records: {
      id: string;
      data: Record<string, any>;
      createdDateTime: string;
      modifiedDateTime: string;
    }[];
  };
}
/**
 * Fetch a list of SDOs.
 */
export const fetchOrganizationSDOs: Thunk<
  {
    readonly limit: number;
    readonly offset: number;
  },
  FetchOrganizationSDOsResponse
> =
  ({ limit, offset }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      query listSDOs($schemaId: ID!, $limit: Int!, $offset: Int, $filter: JSONData) {
        structuredDataObjects(
          schemaId: $schemaId,
          limit: $limit,
          offset: $offset,
          orderBy: [{field: modifiedDateTime, direction: desc}],
          owned: true,
          filter: $filter,
        ) {
          records {
            id
            data
            createdDateTime
            modifiedDateTime
          }
        }
      }
    `;

    return await callGraphQLApi<{
      structuredDataObjects: {
        records: {
          id: string;
          data: Record<string, any>;
          createdDateTime: string;
          modifiedDateTime: string;
        }[];
      };
    }>({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        FETCH_ORGANIZATION_SDOS_SUCCESS,
        FETCH_ORGANIZATION_SDOS_FAILURE,
      ],
      query,
      variables: {
        schemaId,
        limit,
        offset,
        filter: null,
        // filter: status.includes(filter)
        //   ? {
        //       status: startCase(filter),
        //     }
        //   : null,
      },
      dispatch,
      getState,
    });
  };

export interface FetchOrganizationSDOsByTDOsIdResponse {
  structuredDataObjects: {
    records: {
      id: string;
      data: Record<string, any>;
      createdDateTime: string;
      modifiedDateTime: string;
    }[];
  };
}

/**
 * Fetch a SDO by its TDO ID.
 */
export const fetchOrganizationSDOsByTDOsId: Thunk<
  {
    readonly tdoId: TDOId;
  },
  FetchOrganizationSDOsByTDOsIdResponse
> =
  ({ tdoId }) =>
  async (dispatch, getState) => {
    const schemaId = await lookupLatestSchemaId(dispatch, getState);
    if (!schemaId) {
      return;
    }
    const query = `
      query searchSDOs($schemaId: ID!) {
        structuredDataObjects(
          schemaId: $schemaId,
          filter: { tdoId: { eq: "${tdoId}" } },
          limit: 1,
          owned: true
        ) {
          records {
            id
            data
            createdDateTime
            modifiedDateTime
          }
        }
      }
    `;

    return await callGraphQLApi({
      actionTypes: [
        NOOP_ORGANIZATION_ACTION,
        FETCH_ORGANIZATION_SDOS_BY_ID_SUCCESS,
        FETCH_ORGANIZATION_SDOS_BY_ID_FAILURE,
      ],
      query,
      variables: { schemaId, tdoId },
      dispatch,
      getState,
    });
  };

export type FetchOrganizationTDOsResponse = Record<
  string,
  {
    id: TDOId;
    name: string;
    status: 'downloaded' | 'recording' | 'recorded';
    thumbnailUrl: string;
    createdDateTime: string; // ISO
    modifiedDateTime: string; // ISO
    startDateTime: string; // ISO
    stopDateTime: string; // ISO
    details: {
      // TODO: Not all tags are redactionStatus tags
      // readonly tags?: ReadonlyArray<{
      //   redactionStatus: 'Draft' | 'Pending Review' | 'Complete';
      //   value: 'in redaction';
      //   toBeDeletedTime?: string;
      // }>;
      readonly tags?: ReadonlyArray<
        | {
            redactionStatus: string;
            redactionStatusKey?: string;
            value: 'in redaction';
            oldFolderPath?: FolderPathItem[];
            trashRetentionDays?: number;
            /** Legacy stamp on items trashed before the trashRetentionDays rename. */
            numberOfDaysToKeep?: number;
            movedToTrashDateTime?: string;
          }
        | Tag
        | null // We have seen tags be null - unclear how this happens - https://veritone.sentry.io/issues/6625961324/?alert_rule_id=621906&alert_timestamp=1747993663065&alert_type=email&environment=zsfc01-usgoveast1&notification_uuid=14d337a2-cddf-4039-8c6f-688c234c82df&project=1380797&referrer=alert_email
      >;
      readonly govQARequestId?: string;
      readonly foiaXpressRequestId?: string;
      readonly casepointRequestId?: string;
      readonly nuixRequestId?: string;
      readonly exterroRequestId?: string;
      readonly veritoneFile?: VeritoneFile;
    };
    tasks: {
      records: ReadonlyArray<{
        id: string;
        status: TaskStatus;
        createdDateTime: string;
        startedDateTime: string | null;
        completedDateTime: string | null;
        engineId: string;
        engine: {
          id: string;
          categoryId: string;
          name: string;
        };
        jobId: string;
        job: {
          status: JobStatus;
        };
      }>;
    };
    folders?: {
      id: string;
      name: string;
      folderPath: FolderPathItem[];
    }[];
  }
>;
/**
 * Pull the list of TDOs.
 */
export const fetchOrganizationTDOs: Thunk<{
  readonly sdos: ReadonlyArray<{ readonly tdoId: TDOId }>;
}> = (payload) => async (dispatch, getState) => {
  const query = `query mapTDOs {
    ${payload.sdos
      .map(
        ({ tdoId }) => `id${tdoId}: temporalDataObject(id: ${JSON.stringify(
          tdoId
        )}) {
      id
      name
      status
      thumbnailUrl
      createdDateTime
      modifiedDateTime
      startDateTime
      stopDateTime
      details
      folders {
        id
        name
        folderPath {
          id
          name
          parent {
            id
          }
        }
      }
      tasks (limit: 300) {
        records {
          id
          status
          createdDateTime
          startedDateTime
          completedDateTime
          engineId
          engine {
            id
            categoryId
            name
          }
          jobId
          job {
            status
          }
        }
      }
      assets(assetType: ["redacted-media", "redacted-media-clip", "redacted-document"], limit: 1) {
        count
      }
    }`
      )
      .join(',')}
  }`;

  return await callGraphQLApi<FetchOrganizationTDOsResponse>({
    actionTypes: [
      NOOP_ORGANIZATION_ACTION,
      FETCH_ORGANIZATION_TDOS_SUCCESS,
      FETCH_ORGANIZATION_TDOS_FAILURE,
    ],
    query,
    variables: {},
    dispatch,
    getState,
  });
};

export interface FetchOrganizationTDOByIdResponse {
  temporalDataObject: {
    id: TDOId;
    name: string;
    status: 'downloaded' | 'recording' | 'recorded';
    thumbnailUrl: string;
    startDateTime: string;
    stopDateTime: string;
    createdDateTime: string;
    modifiedDateTime: string;
    details: Record<string, any>;
    primaryAsset: {
      id: string;
      contentType: string;
      signedUri: string;
    };
    streams: {
      protocol: string;
      uri: string;
    }[];
    tasks: {
      records: {
        id: string;
        status: TaskStatus;
        createdDateTime: string;
        startedDateTime: string;
        completedDateTime: string;
        engineId: string;
        engine: {
          id: string;
          categoryId: string;
          name: string;
        };
        jobId: string;
        job: {
          status: JobStatus;
        };
      }[];
    };
  };
}
/**
 * Pull a TDO detail.
 */
export const fetchOrganizationTDOById: Thunk<{
  readonly tdoId: TDOId;
}> = (payload) => async (dispatch, getState) => {
  const query = `query fetchTDODetail {
    temporalDataObject(id: ${payload.tdoId}) {
      id
      name
      status
      thumbnailUrl
      startDateTime
      stopDateTime
      createdDateTime
      modifiedDateTime
      details
      primaryAsset(assetType: "media") {
        id
        contentType
        signedUri
      }
      streams {
        protocol
        uri
      }
      tasks (limit: 300) {
        records {
          id
          status
          createdDateTime
          startedDateTime
          completedDateTime
          engineId
          engine {
            id
            categoryId
            name
          }
          jobId
          job {
            status
          }
        }
      }
      assets(assetType: ["redacted-media", "redacted-media-clip", "redacted-document"], limit: 1) {
        count
      }
    }
  }`;

  return await callGraphQLApi<FetchOrganizationTDOByIdResponse>({
    actionTypes: [
      NOOP_ORGANIZATION_ACTION,
      FETCH_ORGANIZATION_TDO_BY_ID_SUCCESS,
      FETCH_ORGANIZATION_TDO_BY_ID_FAILURE,
    ],
    query,
    variables: {},
    dispatch,
    getState,
  });
};

// /**
//  * Use DataRegistry to lookup the latest published SDO Schema ID.
//  * @returns {string} Schema Id
//  */
export const lookupLatestSchemaId = (
  () => async (dispatch: any, getState: () => any) => {
    const {
      tdoRedactFileStatusRegistryId,
      // The one place permitted to read the deprecated key — everywhere else the
      // lint rule should keep flagging it.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      tdoRedactStateSchemaId,
      apiRoot,
    } = getConfig<Window['config']>(getState());

    // `tdoRedactStateSchemaId` is a data registry id despite its name — the
    // Redact File Status registry, one SDO per TDO. The correctly named key wins
    // while both are accepted. See @helpers/registryConfig.
    const dataRegistryId = resolveRenamedRegistryId({
      registryId: tdoRedactFileStatusRegistryId,
      legacyRegistryId: tdoRedactStateSchemaId,
      registryKey: 'tdoRedactFileStatusRegistryId',
      legacyKey: 'tdoRedactStateSchemaId',
    });

    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'tdoRedactFileStatusRegistryId',
      apiRoot
    );
  }
)();

type UTCDateString = string;

export interface FetchOrganizationTasksResponse {
  tasks: {
    records: {
      id: string;
      targetId: TDOId;
      status: TaskStatus;
      jobId: string;
      createdDateTime: string;
      startedDateTime: string | null;
      completedDateTime: string | null;
      engineId: string;
      engine: {
        id: string;
        name: string;
        categoryId: string;
      };
      job: {
        status: JobStatus;
      };
      ioFolders: {
        referenceId: string;
      }[];
    }[];
  };
}

// export interface FetchOrganizationTasksPayload {
//   tasks: {
//     records: {
//       id: string;
//       targetId: TDOId;
//       status: TaskStatus;
//       jobId: string;
//       createdDateTime: string;
//       startedDateTime: string | null;
//       completedDateTime: string | null;
//       engineId: string;
//       engine: {
//         id: string;
//         name: string;
//         categoryId: string;
//       };
//       job: {
//         status: string;
//       };
//       ioFolders: {
//         referenceId: string;
//       }[];
//     }[];
//   };
// }

export const fetchOrganizationTasks: Thunk<
  {
    since: UTCDateString;
  } | void,
  FetchOrganizationTasksResponse
> = (payload) => async (dispatch, getState) => {
  const dateTimeFilter =
    payload && payload.since
      ? `(dateTimeFilter: {field: modifiedDateTime, fromDateTime: "${payload?.since}"})`
      : '';

  const query = `query getOrgTasks {
      tasks ${dateTimeFilter} {
         records {
           id
           targetId
           status
           jobId
           #taskOutput
           createdDateTime
           startedDateTime
           completedDateTime
           engineId
           engine {
             id
             name
             categoryId
           }
           job {
             status
           }
           ioFolders {
             referenceId
           }
         }
       }
     }`;

  return await callGraphQLApi<FetchOrganizationTasksResponse>({
    actionTypes: [
      NOOP_ORGANIZATION_ACTION,
      FETCH_ORGANIZATION_TASKS_SUCCESS,
      FETCH_ORGANIZATION_TASKS_FAILURE,
    ],
    query,
    variables: {},
    dispatch,
    getState,
  });
};

export interface FetchOrganizationJobsResponse {
  jobs: {
    records: {
      id: string;
      targetId: TDOId;
      status: TaskStatus;
      createdDateTime: string;
    }[];
  };
}

export const fetchOrganizationJobs: Thunk<
  {
    since: UTCDateString;
  } | void,
  FetchOrganizationJobsResponse
> = (payload) => async (dispatch, getState) => {
  const dateTimeFilter =
    payload && payload.since
      ? `(dateTimeFilter: {field: modifiedDateTime, fromDateTime: "${payload?.since}"})`
      : '';

  const query = `query getOrgJobs {
   jobs ${dateTimeFilter} {
      records {
        id
        targetId
        status
        createdDateTime
      }
    }
  }`;

  return await callGraphQLApi<FetchOrganizationJobsResponse>({
    actionTypes: [
      NOOP_ORGANIZATION_ACTION,
      FETCH_ORGANIZATION_JOBS_SUCCESS,
      FETCH_ORGANIZATION_JOBS_FAILURE,
    ],
    query,
    variables: {},
    dispatch,
    getState,
  });
};

export interface TDOForExport {
  id: TDOId;
  name: string;
  startDateTime: string;
  stopDateTime: string;
  createdDateTime: string;
  modifiedDateTime: string;
  details: Record<string, any>;
  folders: {
    id: string;
    name: string;
    folderPath: FolderPathItem[];
  }[];
  redactedMediaAssets: {
    records: {
      id: string;
      name: string;
      modifiedDateTime: string;
      details: Record<string, any>;
    }[];
  };
}
/**
 * Fetch all TDOs for export media list on main page or legacy files on folder view.
 */
export const fetchAllTDOsForExport = async (
  getState: () => any
): Promise<TDOForExport[]> => {
  const exportTDOs: TDOForExport[] = [];

  const batchSize = 100;
  let offset = 0;
  let hasMore = true;

  const schemaId = await lookupLatestSchemaId(noop, getState);

  if (!schemaId) {
    throw new Error('Failed to get schema ID');
  }

  const oauthToken = selectOAuthToken(getState());
  const sessionToken = selectSessionToken(getState());
  const { apiRoot, graphQLEndpoint, veritoneAppId } =
    getConfig<Window['config']>(getState());
  const authorization = {
    endpoint: `${apiRoot}/${graphQLEndpoint}`,
    token: sessionToken || oauthToken,
    veritoneAppId,
  };

  while (hasMore) {
    try {
      const sdoQuery = `
        query listSDOs($schemaId: ID!, $limit: Int!, $offset: Int) {
          structuredDataObjects(
            schemaId: $schemaId,
            limit: $limit,
            offset: $offset,
            orderBy: [{field: modifiedDateTime, direction: desc}],
            owned: true
          ) {
            records {
              id
              data
              createdDateTime
              modifiedDateTime
            }
          }
        }
      `;

      const sdoResponse = await fetchGraphQLApi<FetchOrganizationSDOsResponse>({
        ...authorization,
        query: sdoQuery,
        variables: { schemaId, limit: batchSize, offset },
      });

      if (!sdoResponse.data || sdoResponse.errors) {
        throw new Error('Failed to fetch SDOs for export');
      }

      const sdos = sdoResponse.data.structuredDataObjects?.records || [];

      if (sdos.length === 0) {
        break;
      }

      const tdoIds = sdos
        .map((sdo) => sdo.data?.tdoId as string)
        .filter(Boolean);

      const TDO_BATCH_SIZE = 20;
      for (let i = 0; i < tdoIds.length; i += TDO_BATCH_SIZE) {
        const batchIds = tdoIds.slice(i, i + TDO_BATCH_SIZE);
        const tdoQuery = `query mapTDOs {
          ${batchIds
            .map(
              (id) => `id${id}: temporalDataObject(id: ${JSON.stringify(id)}) {
                id
                name
                startDateTime
                stopDateTime
                createdDateTime
                modifiedDateTime
                details
                folders {
                  id
                  name
                  folderPath {
                    id
                    name
                    parent {
                      id
                      name
                    }
                  }
                }
                redactedMediaAssets: assets(
                  limit: 1,
                  orderBy: createdDateTime,
                  orderDirection: desc,
                  assetType: ["redacted-media", "redacted-media-clip"]
                ) {
                  records {
                    id
                    name
                    modifiedDateTime
                    details
                  }
                }
              }`
            )
            .join(',')}
        }`;

        const tdoResponse = await fetchGraphQLApi({
          ...authorization,
          query: tdoQuery,
          variables: {},
        });

        if (tdoResponse.errors?.length) {
          console.error(
            'Export media list: TDO batch fetch errors',
            tdoResponse.errors
          );
        }

        if (tdoResponse.data) {
          const tdosData = tdoResponse.data as Record<string, TDOForExport>;
          Object.entries(tdosData).forEach(([key, value]) => {
            if (key.startsWith('id') && value) {
              exportTDOs.push(value);
            }
          });
        }
      }

      offset += batchSize;

      if (sdos.length < batchSize) {
        hasMore = false;
      }
    } catch (error) {
      console.error('Error fetching TDOs for export:', error);
      throw error;
    }
  }

  return exportTDOs;
};
